import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageLayout } from '@/components/Layout/PageLayout';
import { Button } from '@/components/Common/button';
import { ChatbotSelectorDialog } from '@/components/Chatbots/ChatbotSelectorDialog';
import { chatbotsService, Chatbot } from '@/services/chatbots';
import { conversationsService, Conversation } from '@/services/conversations';
import notify from '@/lib/notify';
import { cn } from '@/lib/utils';
import {
  MessageSquareIcon,
  PlusIcon,
  StarIcon,
  BotIcon,
  CalendarIcon,
  TrashIcon,
} from 'lucide-react';

export const ConversationsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const chatbotIdFromUrl = searchParams.get('chatbot_id');

  const [chatbots, setChatbots] = useState<Chatbot[]>([]);
  const [groupedConversations, setGroupedConversations] = useState<Record<string, Conversation[]>>({});
  const [selectedChatbotId, setSelectedChatbotId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSelectorDialog, setShowSelectorDialog] = useState(false);

  const loadChatbots = useCallback(async () => {
    try {
      const data = await chatbotsService.list();
      setChatbots(data);

      if (chatbotIdFromUrl && data.find((c) => c.id === chatbotIdFromUrl)) {
        setSelectedChatbotId(chatbotIdFromUrl);
      } else if (data.length > 0) {
        const defaultChatbot = data.find((c) => c.is_default) || data[0];
        setSelectedChatbotId(defaultChatbot.id);
      } else {
        setSelectedChatbotId(null);
      }
    } catch (error) {
      console.error('Error loading chatbots:', error);
      notify.error(t('conversationsPage.loadAgentsError'));
    }
  }, [chatbotIdFromUrl, t]);

  const loadConversations = useCallback(async () => {
    try {
      const data = await conversationsService.list();
      const grouped: Record<string, Conversation[]> = {};

      data.forEach((conversation) => {
        const chatbotId = conversation.chatbot_id || 'none';
        if (!grouped[chatbotId]) {
          grouped[chatbotId] = [];
        }
        grouped[chatbotId].push(conversation);
      });

      Object.keys(grouped).forEach((chatbotId) => {
        grouped[chatbotId].sort((a, b) => {
          const dateA = new Date(a.created_at).getTime();
          const dateB = new Date(b.created_at).getTime();
          return dateB - dateA;
        });
      });

      setGroupedConversations(grouped);
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  }, []);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      await Promise.all([loadChatbots(), loadConversations()]);
    } catch (error) {
      console.error('Error loading conversation page data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [loadChatbots, loadConversations]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSelectChatbot = (chatbotId: string) => {
    setSelectedChatbotId(chatbotId);
    setSearchParams({ chatbot_id: chatbotId });
  };

  const handleOpenConversation = (conversationId: string) => {
    navigate(`/chat?conversation_id=${conversationId}`);
  };

  const handleDeleteConversation = async (conversationId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm(t('conversationsPage.confirmDelete'))) return;

    try {
      await conversationsService.delete(conversationId);
      setGroupedConversations((prev) => {
        const next: Record<string, Conversation[]> = {};
        Object.entries(prev).forEach(([chatbotId, convs]) => {
          next[chatbotId] = convs.filter((c) => c.id !== conversationId);
        });
        return next;
      });
      notify.success(t('conversationsPage.deleted'));
    } catch (error) {
      console.error('Error deleting conversation:', error);
      notify.error(t('conversationsPage.deleteError'));
    }
  };

  const handleNewConversation = () => {
    if (chatbots.length === 0) {
      notify.error(t('conversationsPage.createAgentFirst'));
      return;
    }
    setShowSelectorDialog(true);
  };

  const handleSelectChatbotForNewConv = (chatbot: Chatbot) => {
    setShowSelectorDialog(false);
    navigate(`/chat?chatbot_id=${chatbot.id}`);
  };

  const visibleChatbots = chatbots.filter(
    (c) => (groupedConversations[c.id]?.length || 0) > 0
  );

  // Keep the selection pointing at a visible agent once conversations are loaded.
  useEffect(() => {
    if (isLoading) return;
    if (visibleChatbots.length === 0) {
      if (selectedChatbotId !== null) setSelectedChatbotId(null);
      return;
    }
    if (!selectedChatbotId || !visibleChatbots.some((c) => c.id === selectedChatbotId)) {
      const preferred = visibleChatbots.find((c) => c.is_default) || visibleChatbots[0];
      setSelectedChatbotId(preferred.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, chatbots, groupedConversations, selectedChatbotId]);

  const selectedChatbot = chatbots.find((c) => c.id === selectedChatbotId);
  const selectedConversations = selectedChatbotId ? (groupedConversations[selectedChatbotId] || []) : [];

  return (
    <PageLayout
      icon={<MessageSquareIcon className="h-6 w-6" />}
      title={t('conversationsPage.title')}
      subtitle={t('conversationsPage.subtitle')}
      headerRight={(
        <Button onClick={handleNewConversation} size="sm">
          <PlusIcon className="w-4 h-4 mr-2" />
          {t('aiChatbot.newConversation')}
        </Button>
      )}
    >
      {isLoading ? (
        <div className="text-center text-muted-foreground py-12">{t('global.loading')}</div>
      ) : chatbots.length === 0 ? (
        <div className="text-center text-muted-foreground py-12">
          <p className="mb-2">{t('conversationsPage.noAgents')}</p>
          <p className="text-sm">{t('conversationsPage.noAgentsHint')}</p>
        </div>
      ) : visibleChatbots.length === 0 ? (
        <div className="text-center text-muted-foreground py-12">
          <p className="mb-2">{t('conversationsPage.noConversations')}</p>
          <p className="text-sm">{t('conversationsPage.noConversationsHint')}</p>
        </div>
      ) : (
        <div className="flex gap-4 h-[calc(100vh-20rem)]">
          <div className="w-64 bg-card border border-border rounded-lg p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">{t('conversationsPage.agents')}</h3>
            </div>
            <div className="space-y-2">
              {visibleChatbots.map((chatbot) => {
                const conversationCount = groupedConversations[chatbot.id]?.length || 0;
                const isSelected = selectedChatbotId === chatbot.id;

                return (
                  <button
                    key={chatbot.id}
                    title={chatbot.name}
                    onClick={() => handleSelectChatbot(chatbot.id)}
                    className={cn(
                      'w-full text-left px-3 py-2 rounded-lg transition-colors',
                      isSelected
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-muted'
                    )}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <BotIcon className="w-4 h-4 shrink-0" />
                      <span className="font-medium text-sm truncate">{chatbot.name}</span>
                      {chatbot.is_default && (
                        <StarIcon className="w-3 h-3 text-yellow-500 fill-yellow-500 shrink-0" />
                      )}
                    </div>
                    <div className={cn(
                      'text-xs',
                      isSelected ? 'text-primary-foreground/80' : 'text-muted-foreground'
                    )}>
                      {t('conversationsPage.conversationCount', { count: conversationCount })}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex-1 bg-card border border-border rounded-lg p-4 overflow-y-auto">
            {selectedChatbot ? (
              <>
                <div className="mb-4">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    {selectedChatbot.name}
                    {selectedChatbot.is_default && (
                      <StarIcon className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                    )}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    {selectedChatbot.description || t('conversationsPage.noDescription')}
                  </p>
                </div>

                <div className="space-y-3">
                  <div
                    className="bg-background border border-dashed border-border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => navigate(`/chat?chatbot_id=${selectedChatbot.id}`)}
                  >
                    <p className="text-sm text-muted-foreground italic">{t('conversationsPage.newConversationPlaceholder')}</p>
                  </div>

                  {selectedConversations.map((conversation) => (
                    <div
                      key={conversation.id}
                      className="bg-background border border-border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => handleOpenConversation(conversation.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-foreground font-medium">
                            {conversation.title || t('conversationsPage.untitled')}
                          </p>
                          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                            <CalendarIcon className="w-3 h-3" />
                            {new Date(conversation.created_at).toLocaleDateString(i18n.language, {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenConversation(conversation.id);
                            }}
                          >
                            {t('conversationsPage.open')}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-muted-foreground hover:text-destructive"
                            aria-label={t('conversationsPage.deleteAria')}
                            onClick={(e) => handleDeleteConversation(conversation.id, e)}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center text-muted-foreground py-12">
                {t('conversationsPage.selectAgent')}
              </div>
            )}
          </div>
        </div>
      )}

      <ChatbotSelectorDialog
        open={showSelectorDialog}
        onClose={() => setShowSelectorDialog(false)}
        chatbots={chatbots}
        onSelect={handleSelectChatbotForNewConv}
      />
    </PageLayout>
  );
};
